import { FC } from 'react';
import { Box, Text, HStack, useColorModeValue, Flex } from '@chakra-ui/react';
import { ago } from 'src/utils/time-utils';
import UserBadge from 'src/components/common/UserBadge';
import { EventType, HistoryEventGroupDeepFragment } from 'src/gql/graphql';

// Props for the EventContent component
interface EventContentProps {
  eventType: EventType;
  oldName?: string;
  newName?: string;
  details?: string;
  secondaryTextColor: string;
}

const EventContent: FC<EventContentProps> = ({
  eventType,
  oldName,
  newName,
  details,
  secondaryTextColor,
}) => {
  if (eventType === EventType.Modified && oldName && newName) {
    return (
      <Text fontSize="xs" color={secondaryTextColor} mb="1">
        <Text as="del">{oldName}</Text> → {newName}
      </Text>
    );
  }

  if (details) {
    return (
      <Text fontSize="xs" color={secondaryTextColor} mb="1">
        {details}
      </Text>
    );
  }

  return null;
};

// Props for the TimelineEvent component
interface ProjectHistoryTimelineEventProps {
  eventGroup: HistoryEventGroupDeepFragment;
  isSelected: boolean;
  isPreview?: boolean;
  onClick: () => void;
  onMouseDown?: (e: React.MouseEvent) => void;
  t: (key: string, options?: { count: number }) => string;
}

export const ProjectHistoryTimelineEvent: FC<
  ProjectHistoryTimelineEventProps
> = ({
  eventGroup,
  isSelected,
  isPreview = false,
  onClick,
  onMouseDown,
  t,
}) => {
  const {
    eventType,
    resourceType,
    author,
    groupDate,
    affectedResources,
    firstEvent,
    eventCount,
  } = eventGroup;

  const formattedResourceType = resourceType?.includes('SHEET_SHAPE')
    ? 'shape'
    : resourceType;

  const translationKey =
    `group-name.${formattedResourceType}.${eventType}`.toLowerCase();

  const eventGroupName = t(translationKey, {
    count: affectedResources ?? 0,
  });

  // Theme colors
  const textColor = useColorModeValue('gray.800', 'gray.100');
  const secondaryTextColor = useColorModeValue('gray.600', 'gray.400');
  const selectedTextColor = useColorModeValue('blue.700', 'blue.200');
  const previewTextColor = useColorModeValue('gray.600', 'gray.400');

  // Get the right text color based on selection state
  const primaryColor = isSelected
    ? selectedTextColor
    : isPreview
    ? previewTextColor
    : textColor;

  return (
    <Box
      py="2"
      px="4"
      cursor="pointer"
      onClick={onClick}
      onMouseDown={onMouseDown}
      position="relative"
    >
      <Box py="1">
        <Flex mb="1" justifyContent="space-between">
          <Text fontSize="xs" fontWeight="bold" color={primaryColor}>
            {eventGroupName}
          </Text>
          {groupDate && (
            <Text fontSize="xs" color={secondaryTextColor}>
              {ago(groupDate)}
            </Text>
          )}
        </Flex>

        <EventContent
          eventType={eventType || EventType.Modified}
          oldName={eventCount === 1 ? firstEvent?.metadata?.oldName : undefined}
          newName={eventCount === 1 ? firstEvent?.metadata?.newName : undefined}
          // Not sure what to use the details for yet, and how to handle translations
          // details={eventCount === 1 ? firstEvent?.metadata?.details : undefined}
          secondaryTextColor={secondaryTextColor}
        />

        <HStack spacing="1" mt="1">
          <Text fontSize="xs" color={secondaryTextColor}>
            {t('event.created-by')}
          </Text>
          <UserBadge username={author ?? 'Unknown author'} size="2xs" />
        </HStack>
      </Box>
    </Box>
  );
};
